import { createRoute, Link, useNavigate } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { Route as AdminLayoutRoute } from "./_layout";
import { Badge, BadgeWithDot } from "@/components/base/badges/badges";
import { Button } from "@/components/base/buttons/button";
import { InlineAlert } from "@/components/application/alerts/inline-alert";
import {
  DataTable,
  type Column,
} from "@/components/application/data-table/data-table";
import { CreateBudgetDialog } from "../../components/custom/CreateBudgetDialog";
import {
  useAvailableBudgetTargets,
  useBudgetList,
} from "../../hooks/useBudgetQueries";
import { useCreateBudget } from "../../hooks/useBudgetMutations";
import { ApiError } from "../../lib/apiClient";
import {
  formatBudgetMonth,
  formatEur,
  formatKc,
  type BudgetListRow,
  type BudgetStatus,
} from "../../schemas/budget";

/**
 * UC-002 Budget list — Data Table s filter chips per status
 * (draft/approved/returned, Phase 16, REQ-BIZ-007 + REQ-API-002).
 *
 * Click řádek → /admin/budgets/$monthId (TanStack Router Link).
 *
 * Per REQ-UI-018 color-not-only: status Badge má variant + text label.
 * Per princip #4 (sync transparency): `sync_complete=false` rows mají
 * warning indikátor (color + text "neúplný sync").
 */

export const Route = createRoute({
  getParentRoute: () => AdminLayoutRoute,
  path: "/admin/budgets",
  component: AdminBudgetsIndexPage,
});

const FILTER_CHIPS: { id: BudgetStatus | "all"; label: string }[] = [
  { id: "all", label: "Vše" },
  { id: "draft", label: "Draft" },
  { id: "approved", label: "Schválené" },
  { id: "returned", label: "Vrácené" },
];

function colorForStatus(
  status: BudgetStatus,
): "gray" | "blue" | "success" | "warning" | "error" {
  switch (status) {
    case "approved":
      return "success";
    case "draft":
      return "blue";
    case "returned":
      return "warning";
  }
}

function statusLabel(status: BudgetStatus): string {
  switch (status) {
    case "approved":
      return "Schválen";
    case "draft":
      return "Draft";
    case "returned":
      return "Vrácen";
  }
}

function AdminBudgetsIndexPage() {
  const { data, isLoading, error } = useBudgetList();
  const [activeFilter, setActiveFilter] = useState<BudgetStatus | "all">("all");
  const navigate = useNavigate();
  const [createOpen, setCreateOpen] = useState(false);
  const availableTargets = useAvailableBudgetTargets({ enabled: createOpen });
  const createMutation = useCreateBudget();
  const createErr = createMutation.error;
  const createErrCode = createErr instanceof ApiError ? createErr.errorCode : null;
  const createErrMsg = createErr instanceof Error ? createErr.message : null;

  const rows = useMemo<BudgetListRow[]>(() => {
    if (!data?.rows) return [];
    if (activeFilter === "all") return data.rows;
    return data.rows.filter((r) => r.status === activeFilter);
  }, [data, activeFilter]);

  const columns: Column<BudgetListRow>[] = [
    {
      key: "month",
      header: "Měsíc",
      cell: (r) => (
        <Link
          to="/admin/budgets/$monthId"
          params={{ monthId: r.budget_id }}
          className="text-brand-secondary font-medium hover:underline"
          data-testid={`budget-list-link-${r.budget_id}`}
        >
          {formatBudgetMonth(r.month)}
        </Link>
      ),
    },
    {
      key: "zone",
      header: "Středisko",
      cell: (r) => (
        <>
          <strong className="text-primary">{r.billing_group_code}</strong>
          <span className="text-tertiary"> · {r.zone_code}</span>
        </>
      ),
    },
    {
      key: "status",
      header: "Stav",
      cell: (r) => (
        <span className="flex gap-1.5 flex-wrap">
          <BadgeWithDot color={colorForStatus(r.status)} type="pill-color">
            {statusLabel(r.status)}
          </BadgeWithDot>
          {r.eur_advisory_flag && (
            <span data-testid="budget-list-eur-flag">
              <Badge color="blue">EUR</Badge>
            </span>
          )}
          {!r.sync_complete && <Badge color="warning">Neúplný sync</Badge>}
          {r.unassigned_count > 0 && (
            <Badge color="warning">{r.unassigned_count} nepřiř. SPZ</Badge>
          )}
        </span>
      ),
    },
    {
      key: "kc",
      header: "Celkem CZK",
      cell: (r) => formatKc(r.total_amount_kc),
    },
    {
      key: "eur",
      header: "Celkem EUR",
      cell: (r) => (r.eur_advisory_flag ? formatEur(r.total_amount_eur) : "—"),
    },
    {
      key: "approved_at",
      header: "Schváleno",
      cell: (r) =>
        r.approved_at ? (
          <time dateTime={r.approved_at}>{formatDate(r.approved_at)}</time>
        ) : (
          "—"
        ),
    },
  ];

  return (
    <section aria-labelledby="budgets-title" className="max-w-7xl mx-auto space-y-6">
      <header className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="flex flex-col gap-1">
          <h2 id="budgets-title" className="text-2xl font-bold text-primary m-0">
            Rozpočty
          </h2>
          <p className="text-base text-tertiary m-0">
            Per-Středisko měsíční rozpočet s per-SPZ rozkladem.
          </p>
        </div>
        <Button
          color="primary"
          onPress={() => {
            createMutation.reset();
            setCreateOpen(true);
          }}
          data-testid="budget-create-button"
        >
          Nový rozpočet
        </Button>
      </header>

      {error && (
        <InlineAlert variant="error" title="Chyba při načítání rozpočtů">
          {error instanceof Error ? error.message : "Neznámá chyba."}
        </InlineAlert>
      )}

      <div className="flex gap-2 flex-wrap" role="group" aria-label="Filtr stavů rozpočtů">
        {FILTER_CHIPS.map((chip) => {
          const isActive = activeFilter === chip.id;
          return (
            <button
              key={chip.id}
              type="button"
              aria-pressed={isActive}
              onClick={() => setActiveFilter(chip.id)}
              data-testid={`budget-filter-${chip.id}`}
              className={
                "px-3 py-1.5 rounded-full text-sm font-medium border transition-colors " +
                (isActive
                  ? "bg-brand-solid text-white border-brand-solid"
                  : "bg-primary text-primary border-secondary hover:bg-secondary")
              }
            >
              {chip.label}
            </button>
          );
        })}
      </div>

      <DataTable
        caption="Měsíční rozpočty per Středisko"
        captionVisible={false}
        columns={columns}
        rows={rows}
        rowKey={(r) => r.budget_id}
        emptyMessage="Žádné rozpočty odpovídající filtru."
        loading={isLoading}
      />

      <CreateBudgetDialog
        open={createOpen}
        loading={availableTargets.isLoading}
        targets={availableTargets.data?.targets ?? []}
        submitting={createMutation.isPending}
        submitErrorCode={createErrCode}
        submitErrorMessage={createErrMsg}
        onCancel={() => {
          setCreateOpen(false);
          createMutation.reset();
        }}
        onConfirm={(payload) => {
          createMutation.mutate(payload, {
            onSuccess: (resp) => {
              setCreateOpen(false);
              void navigate({
                to: "/admin/budgets/$monthId",
                params: { monthId: resp.budget_id },
              });
            },
          });
        }}
      />
    </section>
  );
}

function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleString("cs-CZ", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}
